import * as React from 'react';
import { Article } from '@phosphor-icons/react';
import { useDebounceValue } from 'usehooks-ts';

import { useSidebar } from '../context';
import { ArticleNavItem } from '../types';
import { ArticleSearch } from './article-search';
import { ArticleTree } from './article-tree';

import { Spinner } from '@/components/ui/spinner';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { useSubstringFilter } from '@/lib/hooks';

/** Debounce applied to the article tree filter + remount key while searching (avoids work every keystroke). */
const ARTICLE_TREE_SEARCH_DEBOUNCE_MS = 400;
/** Stable ref for useDebounceValue options — inline objects reset debounce each render (usehooks-ts #703). */
const ARTICLE_TREE_SEARCH_DEBOUNCE_OPTIONS = { leading: true, trailing: true } as const;

export interface ArticleNavProps {
  className?: string;
  isLoadingArticles?: boolean;
  articles?: ArticleNavItem[];
}

export function ArticleNav({ articles, className, isLoadingArticles }: ArticleNavProps) {
  const { isSearching, searchValue, onAddArticle } = useSidebar();
  const { contains } = useSubstringFilter();
  const trimmedQuery = searchValue.trim();
  const [debouncedTreeQuery] = useDebounceValue(
    isSearching ? trimmedQuery : '',
    isSearching ? ARTICLE_TREE_SEARCH_DEBOUNCE_MS : 0,
    isSearching ? ARTICLE_TREE_SEARCH_DEBOUNCE_OPTIONS : undefined,
  );

  const shouldShowLoadingState = !isSearching && (isLoadingArticles ?? articles === undefined);
  const renderLoadingEmptyState = React.useCallback(
    () => (
      <div
        className="flex min-h-20 items-center justify-center text-muted-foreground"
        role="status"
        aria-live="polite"
        aria-label="Loading articles list"
      >
        <Spinner className="size-5" aria-hidden="true" />
        <span className="sr-only">Loading articles list</span>
      </div>
    ),
    [],
  );

  const noArticles = !shouldShowLoadingState && !isSearching && (!articles || articles.length === 0);

  const filteredArticles = React.useMemo(() => {
    if (isSearching) {
      // `includes('')` is true for every string; avoid showing the full tree with an empty query.
      if (trimmedQuery === '' || debouncedTreeQuery === '') {
        return [];
      }
      const result: ArticleNavItem[] = [];
      const query = debouncedTreeQuery;

      const traverse = (nodes: ArticleNavItem[]) => {
        for (const item of nodes) {
          if (contains(item.label, query)) {
            // Strip children so each hit is one row; otherwise the tree would render nested
            // items AND the same nodes again as separate roots (duplicates).
            result.push({ ...item, hasChildren: false, items: [] });
          }
          if (item.items?.length) {
            traverse(item.items);
          }
        }
      };

      traverse(articles ?? []);

      return result;
    }
    if (articles && articles.length > 0) {
      return articles;
    }

    return [];
  }, [articles, isSearching, trimmedQuery, debouncedTreeQuery, contains]);

  return (
    <div className={cn('flex flex-col gap-0.5 px-4', className)}>
      {noArticles && (
        <Button variant="ghost" className="text-subtlest" onClick={() => onAddArticle?.(null).catch(() => {})}>
          <Article aria-hidden="true" />
          Create first article or folder
        </Button>
      )}

      {shouldShowLoadingState ? (
        <ArticleTree isSearching={isSearching} articles={[]} renderEmptyState={renderLoadingEmptyState} />
      ) : (
        <>
          <ArticleSearch />
          <nav aria-label="Articles navigation">
            <ArticleTree
              key={isSearching ? (trimmedQuery === '' ? 'search:empty' : `search:${debouncedTreeQuery}`) : 'browse'}
              isSearching={isSearching}
              articles={filteredArticles ?? []}
            />
          </nav>
        </>
      )}
    </div>
  );
}
